import { faClockRotateLeft, faClose } from "@fortawesome/free-solid-svg-icons"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import { ButtonIcon } from "../../components/Button/Button"
import { PopupHelpButton } from "../../components/Help/PopupHelpButton"
import ModalPortal from "../../components/Modal/ModalPortal"
import { TimeClockCardGrid } from "../../components/TimeClock/TimeClockCardGrid"
import { TitlePageResponsiveIconSmall } from "../../components/Titles/Titles"
import { SIZE } from "../../configuration/Sizes"
import THEME from "../../configuration/Theme"
import { useTimeClock } from "../../hooks/TimeClock/useTimeClock"
import { getTimeClockAdjustmentCard } from "../../Objects/TimeClock/TimeClockCards"
import type { TimeClockAdjustment } from "../../Objects/TimeClock/TimeClockAdjustment"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import GetSpace from "../../utils/space"

interface TimeClockMyAdjustmentsPopupProps {
  onClose: () => void
}

export const TimeClockMyAdjustmentsPopup: React.FC<TimeClockMyAdjustmentsPopupProps> = ({ onClose }) => {
  const { t, i18n } = useTranslation(["timeClock", "helpPopup"])

  const { getMyAdjustments } = useTimeClock()

  const [adjustments, setAdjustments] = useState<TimeClockAdjustment[]>([])

  const load = useCallback(async () => {
    const result = await getMyAdjustments()

    if (result) setAdjustments(result)
  }, [])

  useEffect(() => {
    void load()
  }, [])

  const cards = useMemo(
    () => adjustments.map((adjustment, index) => getTimeClockAdjustmentCard(adjustment, index, t, i18n.language)),
    [adjustments, t, i18n.language],
  )

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("adjustment.my_requests")}
              icon={faClockRotateLeft}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={`${defaultStyle.ButtonsActions} flex items-center gap-1`}>
                  <PopupHelpButton
                    variant="global"
                    helpKey="pages.time-clock-my-adjustments"
                    title={t("helpPopup:global.timeClockMyAdjustments.title")}
                    description={t("helpPopup:global.timeClockMyAdjustments.description")}
                    bullets={t("helpPopup:global.timeClockMyAdjustments.bullets", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.timeClock.timeClockMyAdjustmentsPopup.button.onClose"
                  />
                </div>
              }
            />

            <GetSpace top={10} />

            <div className="max-h-[72vh] overflow-y-auto p-2">
              <TimeClockCardGrid
                items={cards}
                emptyLabel={t("adjustment.no_requests")}
                descriptionKey="interactionDescriptions:pages.timeClock.timeClockMyAdjustmentsPopup.card.request"
              />
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
